import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CountUp from "react-countup";
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Search, CheckCircle2, Clock, Target, Calendar, 
  MoreVertical, Upload, FileUp, ArrowRightCircle, User, Trash2, Edit,
  LayoutGrid, List, FolderKanban, Columns
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { RecordAttachments } from '../components/RecordAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_STATUSES = ['todo', 'in_progress', 'awaiting_feedback', 'done'];
const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const statusColors = {
  todo: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-amber-100 text-amber-700',
  awaiting_feedback: 'bg-purple-100 text-purple-700',
  done: 'bg-emerald-100 text-emerald-700',
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-rose-100 text-rose-700'
};

export const TasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 1024 ? 'cards' : 'table'));
  
  // Dialog states
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  // Form state
  const [taskForm, setTaskForm] = useState({
    name: '',
    description: '',
    project_id: 'none',
    assignees: [],
    assigned_by: 'none',
    due_date: '',
    priority: 'medium',
    status: 'todo'
  });

  // Convert to Lead form
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'task_conversion',
    notes: ''
  });

  const openEditDialog = (task) => {
  setEditingTask(task)

  setTaskForm({
    name: task.name || '',
    description: task.description || '',
    project_id: task.project_id || 'none',
    assignees: [...new Set([task.assigned_to, ...(task.team_members || [])].filter(Boolean))],
    assigned_by: task.assigned_by || 'none',
    due_date: task.due_date || '',
    priority: task.priority || 'medium',
    status: task.status || 'todo'
  })

  setShowTaskDialog(true)
}

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);

      console.log("Users API response:", usersRes.data);

      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
  if (!taskForm.name) {
    toast.error('Task name is required');
    return;
  }

  try {

    const { assignees, ...rest } = taskForm;
    const payload = {
      ...rest,
    project_id: taskForm.project_id === "none" ? null : taskForm.project_id,
    assigned_to: assignees[0] || null,
    team_members: assignees,
    assigned_by: taskForm.assigned_by === "none" ? null : taskForm.assigned_by,
    due_date: taskForm.due_date || null
    };

    if (editingTask) {
      await axios.put(`${API}/tasks/${editingTask.id}`, payload);
      toast.success('Task updated');
    } else {
      console.log("Task payload:", payload);
      await axios.post(`${API}/tasks`, payload);
      toast.success('Task created');
    }

    setShowTaskDialog(false);
    setEditingTask(null);

    setTaskForm({
      name: '',
      description: '',
      project_id: 'none',
      assignees: [],
      assigned_by: 'none',
      due_date: '',
      priority: 'medium',
      status: 'todo'
    });

    loadData();

  } catch (error) {
    console.error(error);
    toast.error('Failed to save task');
  }
};

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleToggleStatus = async (task) => {
  const newStatus = task.status === "done" ? "todo" : "done";

  try {
    await axios.put(`${API}/tasks/${task.id}`, {
      name: task.name,
      description: task.description,
      project_id: task.project_id,
      task_number: task.task_number,
      assigned_to: task.assigned_to,
      team_members: task.team_members || [],
      assigned_by: task.assigned_by || null,
      start_date: task.start_date,
      due_date: task.due_date,
      tags: task.tags || [],
      reminder_emails: task.reminder_emails || [],
      reminder_sent: task.reminder_sent || false,
      owner_email: task.owner_email,
      priority: task.priority,
      status: newStatus
    });

    loadData();
  } catch (error) {
    console.error(error);
    toast.error("Failed to update task");
  }
};

  const openConvertDialog = (task) => {
    setSelectedTask(task);
    setLeadForm({
      name: task.name || '',
      email: '',
      phone: '',
      company: '',
      source: 'task_conversion',
      notes: `Converted from task: ${task.name}\n\nOriginal Description: ${task.description || 'N/A'}`
    });
    setShowConvertDialog(true);
  };

  const handleConvertToLead = async () => {
    if (!leadForm.name) {
      toast.error('Lead name is required');
      return;
    }
    try {
      await axios.post(`${API}/leads`, {
        ...leadForm,
        status: 'new',
        converted_from_task_id: selectedTask?.id
      });
      toast.success('Task converted to Lead successfully!');
      setShowConvertDialog(false);
      setSelectedTask(null);
      // Optionally mark task as done
      if (selectedTask) {
        await axios.put(`${API}/tasks/${selectedTask.id}`, { ...selectedTask, status: 'done' });
        loadData();
      }
    } catch (error) {
      toast.error('Failed to convert to lead');
    }
  };

  // Filter tasks
  const isOverdue = (t) => t.due_date && t.due_date.slice(0, 10) < new Date().toLocaleDateString('en-CA') && t.status !== 'done';
  const taskCategory = (t) => isOverdue(t) ? 'overdue' : t.status;
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || taskCategory(task) === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const CATEGORIES = [
    { key: 'todo', label: 'To Do', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
    { key: 'in_progress', label: 'In Progress', tone: 'bg-blue-100 text-blue-700 border-blue-300' },
    { key: 'awaiting_feedback', label: 'Awaiting Feedback', tone: 'bg-purple-100 text-purple-700 border-purple-300' },
    { key: 'overdue', label: 'Overdue', tone: 'bg-red-100 text-red-700 border-red-300' },
    { key: 'done', label: 'Done', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  ];
  const categoryCounts = tasks.reduce((acc, t) => { const c = taskCategory(t); acc[c] = (acc[c] || 0) + 1; return acc; }, {});

  // Stats
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'done').length;
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
  const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'done').length;
   const taskProgress = totalTasks > 0 
  ? Math.round((completedTasks / totalTasks) * 100)
  : 0;

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || '-';
  };

  const getAssigneeName = (userId) => {
    const u = users.find(usr => usr.id === userId);
    return u?.name || '-';
  };

  const getAssigneeNames = (task) => {
    const ids = [...new Set([task.assigned_to, ...(task.team_members || [])].filter(Boolean))];
    if (ids.length === 0) return '-';
    return ids.map(id => users.find(u => u.id === id)?.name || id).join(', ');
  };

  const toggleAssignee = (userId) => {
    setTaskForm(f => ({
      ...f,
      assignees: f.assignees.includes(userId)
        ? f.assignees.filter(id => id !== userId)
        : [...f.assignees, userId]
    }));
  };

  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';

  const handleMoveTask = async (taskId, newStatus) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.status === newStatus) return;
    setTasks(ts => ts.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      toast.success(`Task moved to ${newStatus.replace(/_/g, ' ')}`);
    } catch (e) {
      setTasks(ts => ts.map(t => t.id === taskId ? { ...t, status: task.status } : t));
      toast.error('Failed to move task');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading tasks...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tasks</h1>
          <p className="text-muted-foreground">Manage and track all tasks across your organization</p>
        </div>
        <Button onClick={() => { setEditingTask(null); setTaskForm({ name: '', description: '', project_id: 'none', assignees: [], assigned_by: 'none', due_date: '', priority: 'medium', status: 'todo' }); setShowTaskDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Task
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-black text-[#D4E100] flex items-center justify-center">
              <Target className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold font-mono">
  <CountUp end={totalTasks} duration={1} />
</p>
              <p className="text-sm text-muted-foreground">Total Tasks</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold font-mono">
  <CountUp end={inProgressTasks} duration={1} />
</p>
              <p className="text-sm text-muted-foreground">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold font-mono">
  <CountUp end={completedTasks} duration={1} />
</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{overdueTasks}</p>
              <p className="text-sm text-muted-foreground">Overdue</p>
            </div>
          </CardContent>
        </Card>
        <Card>
  <CardContent className="p-4 flex items-center gap-4">
    
    <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
      <Target className="h-6 w-6" />
    </div>

    <div className="flex-1">
      <p className="text-2xl font-bold">{taskProgress}%</p>
      <p className="text-sm text-muted-foreground">Task Progress</p>

      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
        <div
          className="bg-[#D4E100] h-2 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${taskProgress}%` }}
        />
      </div>

    </div>

  </CardContent>
</Card>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="task-category-chips">
        <button type="button" onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${statusFilter === 'all' ? 'bg-black text-white border-black' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
          data-testid="category-chip-all">
          All ({tasks.length})
        </button>
        {CATEGORIES.map(c => (
          <button key={c.key} type="button" onClick={() => setStatusFilter(statusFilter === c.key ? 'all' : c.key)}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${statusFilter === c.key ? c.tone + ' ring-2 ring-offset-1 ring-slate-400' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            data-testid={`category-chip-${c.key}`}>
            {c.label} ({categoryCounts[c.key] || 0})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search tasks..." 
            className="pl-10" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="tasks-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="status-option-all">All</SelectItem>
            <SelectItem value="todo" data-testid="status-option-todo">To Do</SelectItem>
            <SelectItem value="in_progress" data-testid="status-option-in_progress">In Progress</SelectItem>
            <SelectItem value="awaiting_feedback" data-testid="status-option-awaiting_feedback">Awaiting Feedback</SelectItem>
            <SelectItem value="overdue" data-testid="status-option-overdue">Overdue</SelectItem>
            <SelectItem value="done" data-testid="status-option-done">Done</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40" data-testid="tasks-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {TASK_PRIORITIES.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center border rounded-md overflow-hidden ml-auto" data-testid="tasks-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`p-2.5 ${viewMode === 'table' ? 'bg-black text-[#D4E100]' : 'bg-white text-slate-500'}`}
            data-testid="tasks-view-table-btn"
            aria-label="Table view"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('cards')}
            className={`p-2.5 ${viewMode === 'cards' ? 'bg-black text-[#D4E100]' : 'bg-white text-slate-500'}`}
            data-testid="tasks-view-cards-btn"
            aria-label="Card view"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('board')}
            className={`p-2.5 ${viewMode === 'board' ? 'bg-black text-[#D4E100]' : 'bg-white text-slate-500'}`}
            data-testid="tasks-view-board-btn"
            aria-label="Board view"
          >
            <Columns className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tasks Kanban Board */}
      {viewMode === 'board' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="tasks-board-view">
          {['todo', 'in_progress', 'awaiting_feedback', 'overdue', 'done'].map(status => {
            const colTasks = status === 'overdue'
              ? filteredTasks.filter(isOverdue)
              : filteredTasks.filter(t => t.status === status && !(status !== 'done' && isOverdue(t)));
            return (
              <div
                key={status}
                className="kanban-column space-y-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (status === 'overdue') { toast.info('Overdue is automatic — change the due date or status instead'); return; }
                  const taskId = e.dataTransfer.getData('text/task-id');
                  if (taskId) handleMoveTask(taskId, status);
                }}
                data-testid={`board-column-${status}`}
              >
                <div className="flex items-center justify-between px-1">
                  <p className={`font-semibold text-sm capitalize flex items-center gap-2 ${status === 'overdue' ? 'text-red-600' : ''}`}>
                    {status.replace(/_/g, ' ')}
                    <Badge variant="outline" className={`font-mono ${status === 'overdue' && colTasks.length > 0 ? 'border-red-300 text-red-600' : ''}`}>{colTasks.length}</Badge>
                  </p>
                </div>
                {colTasks.map(task => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/task-id', task.id)}
                    className="rounded-xl border bg-card p-3 space-y-2 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow duration-200"
                    data-testid={`board-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium ${task.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{task.name}</p>
                      <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1 min-w-0">
                        <User className="h-3 w-3 shrink-0" />
                        <span className="truncate max-w-[120px]">{getAssigneeNames(task)}</span>
                      </span>
                      {task.project_id && (
                        <span className="flex items-center gap-1 min-w-0">
                          <FolderKanban className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[110px]">{getProjectName(task.project_id)}</span>
                        </span>
                      )}
                      {task.due_date && (
                        <span className={`flex items-center gap-1 ${new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-600 font-medium' : ''}`}>
                          <Calendar className="h-3 w-3" />
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEditDialog(task)} data-testid={`board-task-edit-${task.id}`}>
                        <Edit className="h-3 w-3 mr-1" /> Edit
                      </Button>
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-xl">Drop tasks here</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tasks Card View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tasks-card-view">
          {filteredTasks.map(task => (
            <Card key={task.id} data-testid={`task-card-${task.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    className="mt-1 h-5 w-5"
                    checked={task.status === 'done'}
                    onCheckedChange={() => handleToggleStatus(task)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${task.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{task.name}</p>
                    {task.description && <p className="text-xs text-muted-foreground line-clamp-2">{task.description}</p>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="-mr-2" data-testid={`task-card-menu-${task.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(task)}>
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openConvertDialog(task)} className="text-blue-600">
                        <ArrowRightCircle className="h-4 w-4 mr-2" /> Convert to Lead
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDeleteTask(task.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                  <Badge className={statusColors[task.status]}>{task.status?.replace('_', ' ')}</Badge>
                  {isOverdue(task) && <Badge className="bg-red-100 text-red-700">overdue</Badge>}
                  {task.due_date && (
                    <span className={`text-xs flex items-center gap-1 ${new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                      <Calendar className="h-3 w-3" />
                      {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 min-w-0">
                    <FolderKanban className="h-3 w-3 shrink-0" />
                    <span className="truncate">{getProjectName(task.project_id)}</span>
                  </span>
                  <span className="flex items-center gap-1 min-w-0">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate">{getAssigneeNames(task)}</span>
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredTasks.length === 0 && (
            <div className="col-span-full text-center py-10 text-muted-foreground border rounded-lg bg-white">
              No tasks found. Create one to get started.
            </div>
          )}
        </div>
      )}

      {/* Tasks Table */}
      {viewMode === 'table' && (
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.map(task => (
                <TableRow key={task.id}>
                  <TableCell>
                    <Checkbox 
                      checked={task.status === 'done'}
                      onCheckedChange={() => handleToggleStatus(task)}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className={`font-medium ${task.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{task.name}</p>
                      {task.description && <p className="text-xs text-muted-foreground line-clamp-1">{task.description}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{getProjectName(task.project_id)}</TableCell>
                  <TableCell className="text-sm">
                    <span>{getAssigneeNames(task)}</span>
                    {task.assigned_by && <p className="text-[11px] text-muted-foreground">by {getAssigneeName(task.assigned_by)}</p>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {task.due_date ? (
                      <span className={new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-600 font-medium' : ''}>
                        {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[task.status]}>{task.status?.replace('_', ' ')}</Badge>
                    {isOverdue(task) && <Badge className="bg-red-100 text-red-700 ml-1">overdue</Badge>}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(task)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openConvertDialog(task)} className="text-blue-600">
                          <ArrowRightCircle className="h-4 w-4 mr-2" /> Convert to Lead
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeleteTask(task.id)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No tasks found. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Create/Edit Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task Name *</Label>
              <Input 
                value={taskForm.name}
                onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                placeholder="Enter task name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={taskForm.project_id} onValueChange={(v) => setTaskForm({ ...taskForm, project_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigned To (select one or more)</Label>
                <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-1" data-testid="task-assignees-list">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-muted/60 cursor-pointer">
                      <Checkbox
                        checked={taskForm.assignees.includes(u.id)}
                        onCheckedChange={() => toggleAssignee(u.id)}
                        data-testid={`task-assignee-${u.id}`}
                      />
                      <span>{u.name}</span>
                    </label>
                  ))}
                </div>
                {taskForm.assignees.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {taskForm.assignees.length} assignee{taskForm.assignees.length > 1 ? 's' : ''}: {taskForm.assignees.map(id => users.find(u => u.id === id)?.name).filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            </div>
            {isAdminUser && (
              <div className="space-y-2">
                <Label>Assign on behalf of</Label>
                <Select value={taskForm.assigned_by} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_by: v })}>
                  <SelectTrigger data-testid="task-assigned-by-select">
                    <SelectValue placeholder="Myself" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Myself</SelectItem>
                    {users.filter(u => u.id !== user?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">The task notification will show it was assigned by this person.</p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input 
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {editingTask?.id && (
            <div className="border-t pt-3 mt-2">
              <RecordAttachments parentType="task" parentId={editingTask.id} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask}>{editingTask ? 'Update Task' : 'Create Task'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Lead Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightCircle className="h-5 w-5 text-blue-600" />
              Convert Task to Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              Converting task: <strong>{selectedTask?.name}</strong>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lead Name *</Label>
                <Input 
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  placeholder="Contact name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input 
                  value={leadForm.company}
                  onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                  placeholder="Company name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  placeholder="+27..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={leadForm.notes}
                onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button onClick={handleConvertToLead} className="bg-blue-600 hover:bg-blue-700">
              <ArrowRightCircle className="h-4 w-4 mr-2" /> Convert to Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
